import { useState, useRef, useEffect, useMemo } from "react";
import useDBO from "../hooks/useDBO";
import { BookOpen, FileText, ChevronDown, Check, Search as SearchIcon, X } from "lucide-react";
import { usePrimaryColor } from "./PrimaryColorContext";

/**
 * SyllabusSelect Component
 * Premium floating dropdown select with search, keyboard navigation, and Tailwind styling.
 * Renders as a floating overlay that never pushes page content.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.value - Currently selected syllabus object
 * @param {Function} props.setValue - Callback to update selected syllabus
 * @param {string} [props.label] - Optional label above the select
 * @param {string} [props.url] - API URL for persisting changes
 * @param {string} [props.field] - Field name to update on server
 * @param {string} [props.size="medium"] - Size variant: "small", "medium", "large"
 *
 * @author Forge
 * @date 2026-04-10
 * @version 4.0.0
 */
export default function SyllabusSelect({
  value,
  setValue,
  label,
  url,
  field,
  size = "medium",
}) {
  const syllabuses = useDBO("/syllabus");
  const { primaryColor } = usePrimaryColor();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const sizeClasses = {
    small: "px-3 py-2 text-[0.8rem]",
    medium: "px-3.5 py-2.5 text-[0.85rem]",
    large: "px-4 py-3 text-[0.9rem]",
  };

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setHighlightIndex(-1);
    const timer = setTimeout(() => {
      searchRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [search]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!syllabuses || syllabuses.loading || syllabuses.error || !syllabuses.get) return [];
    const data = syllabuses.get;
    const q = search.toLowerCase().trim();
    if (!q) return [...data];
    return data.filter(
      (s) =>
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.level && s.level.toLowerCase().includes(q))
    );
  }, [syllabuses, search]);

  const handleSelect = (syllabus) => {
    setValue(syllabus);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filtered.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filtered.length - 1
      );
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    }
  };

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("[data-syllabus-item]");
      items[highlightIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  if (syllabuses.loading) {
    return (
      <div className="flex flex-col gap-2">
        {label && (
          <div className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-wider">
            {label}
          </div>
        )}
        <div className="bg-slate-50 border border-slate-200 rounded-[10px] px-3.5 py-2.5 cursor-not-allowed">
          <div className="flex flex-col gap-2 animate-pulse">
            <div className="h-3.5 bg-slate-200 rounded w-3/4" />
            <div className="h-2.5 bg-slate-100 rounded w-2/5" />
          </div>
        </div>
      </div>
    );
  }

  if (syllabuses.error) {
    return (
      <div className="flex flex-col gap-2">
        {label && (
          <div className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-wider">
            {label}
          </div>
        )}
        <div className="bg-red-50 border border-red-200 rounded-[10px] px-3.5 py-2.5 flex items-center gap-2">
          <span className="text-[0.85rem] font-semibold text-red-600">
            Error loading syllabuses
          </span>
          <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500 text-white rounded-full text-[0.7rem] font-bold">
            !
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 relative" ref={wrapperRef}>
      {label && (
        <div className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </div>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`w-full bg-white border rounded-[10px] font-medium text-slate-800 cursor-pointer flex items-center justify-between gap-2 text-left transition-all duration-150 ${
          sizeClasses[size] || sizeClasses.medium
        } ${
          isOpen
            ? `border-${primaryColor}-800 ring-[3px] ring-[rgba(0,0,0,0.04)]`
            : "border-slate-200 hover:border-slate-300 hover:bg-[#f8fafc]"
        }`}
      >
        <div className="flex-1 min-w-0">
          {value ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-[0.9rem] font-bold text-slate-900 tracking-tight truncate">
                  {value.name}
                </span>
                {value.level && (
                  <span className={`flex-shrink-0 text-[0.6rem] font-bold text-${primaryColor}-800 bg-${primaryColor}-50 px-2 py-0.5 rounded-full uppercase tracking-wider`}>
                    {value.level}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                  <BookOpen size={10} strokeWidth={2.5} />
                  {value.units?.length || 0} units
                </span>
                <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-md">
                  <FileText size={10} strokeWidth={2.5} />
                  {value.lessons?.length || 0} lessons
                </span>
              </div>
            </div>
          ) : (
            <span className="text-[0.85rem] text-slate-400 italic">
              Select a syllabus
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          className={`flex-shrink-0 text-slate-400 transition-transform duration-150 ${
            isOpen ? `rotate-180 text-${primaryColor}-800` : ""
          }`}
        />
      </button>

      {/* Floating Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50 overflow-hidden animate-syllDropIn">
          {/* Search Field */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3">
                <SearchIcon
                  size={14}
                  className="text-slate-400"
                />
              </div>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search syllabi..."
                onKeyDown={handleKeyDown}
                className={`w-full bg-[#f8fafc] border border-slate-200 rounded-[8px] text-slate-800 text-[0.8rem] placeholder-slate-400 focus:outline-none focus:border-${primaryColor}-800 focus:ring-2 focus:ring-[rgba(0,0,0,0.03)] h-8 pl-8 pr-7 transition-all duration-150`}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  tabIndex={-1}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="max-h-[280px] overflow-y-auto"
          >
            {filtered.length === 0 ? (
              <div className="py-8 flex flex-col items-center gap-2">
                <BookOpen size={28} strokeWidth={1.5} className="text-slate-300" />
                <span className="text-[0.8rem] text-slate-400 italic">
                  {search ? "No results found" : "No syllabi available"}
                </span>
              </div>
            ) : (
              filtered.map((syllabus, idx) => {
                const isSelected = value?._id === syllabus._id;
                const isHighlighted = idx === highlightIndex;
                return (
                  <div
                    key={syllabus._id}
                    data-syllabus-item
                    onClick={() => handleSelect(syllabus)}
                    className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] ${
                      isSelected
                        ? `bg-${primaryColor}-50 border-l-[${primaryColor}]`
                        : isHighlighted
                        ? `bg-[#f8fafc] border-l-[${primaryColor}]`
                        : "border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-[0.85rem] font-bold text-slate-900 truncate">
                          {syllabus.name}
                        </span>
                        {syllabus.level && (
                          <span className={`flex-shrink-0 text-[0.55rem] font-bold text-${primaryColor}-800 bg-${primaryColor}-50 px-1.5 py-px rounded-full uppercase tracking-wider`}>
                            {syllabus.level}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-${primaryColor}-800 flex items-center justify-center text-white ml-2`}>
                          <Check size={12} strokeWidth={3} />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold text-emerald-700 bg-emerald-50/80 px-1.5 py-0.5 rounded-md">
                        <BookOpen size={9} strokeWidth={2.5} />
                        {syllabus.units?.length || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold text-violet-700 bg-violet-50/80 px-1.5 py-0.5 rounded-md">
                        <FileText size={9} strokeWidth={2.5} />
                        {syllabus.lessons?.length || 0}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer count */}
          {filtered.length > 0 && (
            <div className="px-3.5 py-1.5 border-t border-slate-100 bg-slate-50/50">
              <span className="text-[0.6rem] text-slate-400 font-medium">
                {filtered.length} syllabus{filtered.length !== 1 ? "s" : ""}
                {search ? ` matching "${search}"` : ""}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
